import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch } from '@/app/store/store';
import { useGetProductDetailQuery } from '../../infra/adapters/pdp.adapter';
import {
  isPriceBreakupIncomplete,
  plainObjectToProductDetail,
} from '../../infra/mappers/pdp.mapper';
import {
  clearPdpInitialData,
  selectPdpInitialData,
  type PdpInitialState,
  PDP_INITIAL_SLICE_NAME,
} from '@/app/store/pdpInitialSlice';
import type { ProductDetail } from '../../domain/models/ProductDetail';

export interface UsePdpServiceOptions {
  designId: string;
}

export interface UsePdpServiceReturn {
  productDetail: ProductDetail | null;
  isLoading: boolean;
  error: unknown;
}

/**
 * Reads PDP data from Redux/RTK cache.
 * SSR seeds the pdpInitial slice; client does not refetch on hydration.
 * Falls back to RTK Query cache for client-side navigations.
 */
export function usePdpService({ designId }: UsePdpServiceOptions): UsePdpServiceReturn {
  const dispatch = useDispatch<AppDispatch>();
  const ssrData = useSelector((state: { [PDP_INITIAL_SLICE_NAME]?: PdpInitialState }) =>
    selectPdpInitialData(state)
  );
  const ssrSnapshotRef = useRef<ProductDetail | null>(null);
  const [forceNetworkFetch, setForceNetworkFetch] = useState(false);

  if (ssrSnapshotRef.current && String(ssrSnapshotRef.current.designId) !== designId) {
    ssrSnapshotRef.current = null;
    setForceNetworkFetch(false);
  }

  if (ssrData !== null && String(ssrData.designId) === designId) {
    ssrSnapshotRef.current = plainObjectToProductDetail(
      ssrData as unknown as Record<string, unknown>
    );
  }

  useEffect(() => {
    if (ssrSnapshotRef.current && isPriceBreakupIncomplete(ssrSnapshotRef.current)) {
      setForceNetworkFetch(true);
    }
    if (ssrData != null) {
      dispatch(clearPdpInitialData());
    }
    // Run once after first render to drop SSR data so remounts refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const skipRtk =
    !forceNetworkFetch &&
    ssrSnapshotRef.current !== null &&
    String(ssrSnapshotRef.current.designId) === designId;

  const { data: cachedData, isLoading, error } = useGetProductDetailQuery(
    { designId },
    { refetchOnMountOrArgChange: forceNetworkFetch, skip: skipRtk }
  );

  const productDetail = useMemo<ProductDetail | null>(() => {
    const snapshot = ssrSnapshotRef.current;
    if (!forceNetworkFetch && snapshot && String(snapshot.designId) === designId) {
      return snapshot;
    }
    if (cachedData && typeof cachedData === 'object') {
      try {
        return plainObjectToProductDetail(cachedData as Record<string, unknown>);
      } catch {
        return null;
      }
    }
    return null;
  }, [cachedData, designId, forceNetworkFetch]);

  return {
    productDetail,
    isLoading: skipRtk ? false : isLoading,
    error: skipRtk ? null : error,
  };
}
